import * as React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AlertTriangle, Check, ClipboardPaste, Download, Upload, X } from 'lucide-react';
import { api } from '@/lib/api';
import { useDebounce } from '@/hooks/useDebounce';
import { cn, numero } from '@/lib/utils';
import { Botao } from '@/componentes/ui/botao';
import type { ProdutoEscolhido } from './CampoProduto';
import { lerTexto, MAX_LINHAS, MODELO_LISTA } from './lerLista';
import { Localizador } from './Localizador';
import type { ConferenciaLocalizadores } from './localizadoresCentros';

/**
 * "Colar lista" da transferência: produto e quantidade em lote.
 *
 * Um gesto cobre os dois casos que chegam no dia a dia:
 *   - o TXT (colado ou solto aqui como arquivo), em qualquer separador;
 *   - a PLANILHA: células copiadas do Excel ou do Google Sheets chegam como
 *     texto separado por TAB. Não precisa de upload nem de biblioteca.
 *
 * Primeira coluna é o código (interno ou EAN), segunda a quantidade. Linha só
 * com o código conta 1 — é o que sai de um leitor bipando peça a peça.
 * Terceira e quarta, opcionais: o CÓDIGO do localizador de origem e o de
 * destino daquela linha. Sem elas, a linha herda o padrão do cabeçalho.
 *
 * Os localizadores são conferidos linha a linha no servidor, com a mesma
 * régua da criação: existe, é do centro certo (origem do centro de origem,
 * destino do de destino) e o saldo do produto no localizador de origem.
 * Localizador próprio que não serve tira a linha; o que vem do padrão (ou
 * falta) só avisa — corrige-se no cabeçalho, depois de importar.
 *
 * NADA É GRAVADO AQUI. A prévia só resolve os códigos e mostra o que vai
 * entrar; "Adicionar" preenche a mesma lista de itens do formulário, e a
 * criação segue pelo caminho de sempre, com as validações de sempre.
 *
 * O que a prévia decidiu linha a linha vai junto na criação e fica na
 * auditoria (`transferencia.importar`), aparecendo no detalhe da
 * transferência.
 */

export interface ItemImportado {
  produto: ProdutoEscolhido;
  quantidade: number;
  /** Os da 3ª/4ª coluna. `null` = herda o padrão do cabeçalho. */
  localizadorOrigem: string | null;
  localizadorDestino: string | null;
}

export type SituacaoLinha =
  | 'ok'
  | 'escolhido'
  | 'nao_encontrado'
  | 'invalida'
  | 'descartada'
  | 'cabecalho';

/** Uma linha como vai para o log — o espelho de `LinhaImportada` do servidor. */
export interface LinhaDoLog {
  linha: number;
  texto: string;
  codigo: string | null;
  quantidade: number | null;
  situacao: SituacaoLinha;
  produto: number | null;
  via: 'codigo' | 'ean' | null;
  candidatos?: number[];
  origem?: string | null;
  destino?: string | null;
  motivo?: string | null;
}

export interface RegistroImportacao {
  arquivo: string | null;
  texto: string;
  linhas: LinhaDoLog[];
}

interface Candidato {
  produto: number;
  descricao: string | null;
  via: 'codigo' | 'ean';
  saldo: number | null;
}

/** TXT do Windows costuma vir em ANSI; UTF-8 primeiro, senão Windows-1252. */
async function lerArquivo(f: File): Promise<string> {
  const bytes = await f.arrayBuffer();
  try {
    return new TextDecoder('utf-8', { fatal: true }).decode(bytes);
  } catch {
    return new TextDecoder('windows-1252').decode(bytes);
  }
}

function baixar(nome: string, texto: string): void {
  const url = URL.createObjectURL(new Blob([texto], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = nome;
  a.click();
  URL.revokeObjectURL(url);
}

export function ImportarLista({
  centroOrigem,
  centroDestino,
  padroes,
  aoAdicionar,
  aoFechar,
}: {
  centroOrigem: number | null;
  centroDestino: number | null;
  /** Os localizadores padrão do cabeçalho, como digitados. */
  padroes: { origem: string; destino: string };
  aoAdicionar: (itens: ItemImportado[], registro: RegistroImportacao) => void;
  aoFechar: () => void;
}): JSX.Element {
  const [texto, definirTexto] = React.useState('');
  const [arquivo, definirArquivo] = React.useState<string | null>(null);
  const [aviso, definirAviso] = React.useState<string | null>(null);
  /** Linha → produto escolhido, para código ambíguo. */
  const [escolhas, definirEscolhas] = React.useState<Record<number, number>>({});
  const [descartadas, definirDescartadas] = React.useState<Set<number>>(new Set());
  const seletorArquivo = React.useRef<HTMLInputElement>(null);

  const atrasado = useDebounce(texto, 350);
  const lidas = React.useMemo(() => lerTexto(atrasado), [atrasado]);
  const codigos = React.useMemo(
    () => [...new Set(lidas.filter((l) => !l.problema && !l.cabecalho).map((l) => l.codigo!))],
    [lidas],
  );
  const grande = lidas.length > MAX_LINHAS;

  const consulta = useQuery<{ codigos: { codigo: string; candidatos: Candidato[] }[] }>({
    queryKey: ['produtos-resolver', centroOrigem, codigos],
    queryFn: () =>
      api.post<{ codigos: { codigo: string; candidatos: Candidato[] }[] }>(
        '/api/produtos/resolver',
        { codigos, centro: centroOrigem },
      ),
    enabled: codigos.length > 0 && !grande,
    staleTime: 60_000,
  });

  const porCodigo = React.useMemo(
    () => new Map((consulta.data?.codigos ?? []).map((c) => [c.codigo, c.candidatos])),
    [consulta.data],
  );

  // Texto novo, decisões novas: escolha da linha 7 não vale para outra lista.
  React.useEffect(() => {
    definirEscolhas({});
    definirDescartadas(new Set());
  }, [atrasado]);

  const resolvendo = codigos.length > 0 && !grande && (consulta.isLoading || texto !== atrasado);

  /** Cada linha com o produto resolvido — ainda sem os localizadores. */
  const comProduto = lidas.map((l) => {
    const candidatos = l.codigo && !l.problema ? (porCodigo.get(l.codigo) ?? []) : [];
    const escolhido = escolhas[l.linha];
    const produto =
      candidatos.length === 1
        ? candidatos[0]!
        : (candidatos.find((c) => c.produto === escolhido) ?? null);
    let situacao: SituacaoLinha | 'escolher';
    if (l.cabecalho) situacao = 'cabecalho';
    else if (descartadas.has(l.linha)) situacao = 'descartada';
    else if (l.problema) situacao = 'invalida';
    else if (candidatos.length === 0) situacao = 'nao_encontrado';
    else if (candidatos.length === 1) situacao = 'ok';
    else situacao = produto ? 'escolhido' : 'escolher';
    return { ...l, candidatos, produto, situacao };
  });

  /*
   * A conferência dos localizadores — a mesma rota que o formulário usa. Só
   * das linhas que já têm produto; a linha sem localizador próprio vai vazia
   * e o servidor aplica o padrão do cabeçalho.
   */
  const paraConferir = comProduto
    .filter((l) => (l.situacao === 'ok' || l.situacao === 'escolhido') && l.produto)
    .map((l) => ({
      chave: l.linha,
      produto: l.produto!.produto,
      origem: l.origem ?? '',
      destino: l.destino ?? '',
      quantidade: l.quantidade ?? 0,
    }));
  const conferenciaLigada =
    centroOrigem !== null && centroDestino !== null && paraConferir.length > 0 && !grande;
  const localizadores = useQuery<ConferenciaLocalizadores>({
    queryKey: [
      'transferencia-conferir-localizadores',
      centroOrigem,
      centroDestino,
      padroes,
      paraConferir,
    ],
    queryFn: () =>
      api.post<ConferenciaLocalizadores>('/api/transferencias/conferir-localizadores', {
        centroOrigem,
        centroDestino,
        padroes,
        linhas: paraConferir,
      }),
    enabled: conferenciaLigada,
    staleTime: 10_000,
  });
  const locPorLinha = React.useMemo(
    () => new Map((localizadores.data?.linhas ?? []).map((l) => [l.chave, l])),
    [localizadores.data],
  );
  const conferindoLocalizadores = conferenciaLigada && localizadores.isFetching;

  /**
   * Cada linha com a sua situação final. Localizador PRÓPRIO que não serve
   * (inexistente, de outro centro) tira a linha, como código não encontrado;
   * o que vem do padrão só avisa.
   */
  const linhas = comProduto.map((l) => {
    const loc = locPorLinha.get(l.linha) ?? null;
    const proprioRuim =
      loc && (l.situacao === 'ok' || l.situacao === 'escolhido')
        ? ([loc.origem, loc.destino].find((p) => p.erro && !p.herdada)?.erro ?? null)
        : null;
    return proprioRuim
      ? { ...l, loc, situacao: 'invalida' as const, problema: proprioRuim }
      : { ...l, loc };
  });

  /*
   * Mesmo produto em várias linhas vira um item só, com a soma — desde que
   * venha dos MESMOS localizadores. De prateleiras diferentes são itens
   * diferentes, como no Procfit.
   */
  const somados = new Map<
    string,
    { candidato: Candidato; quantidade: number; origem: string | null; destino: string | null }
  >();
  const totalPorProduto = new Map<number, number>();
  for (const l of linhas) {
    if ((l.situacao !== 'ok' && l.situacao !== 'escolhido') || !l.produto) continue;
    const k = `${l.produto.produto}|${l.origem ?? ''}|${l.destino ?? ''}`;
    const atual = somados.get(k);
    somados.set(k, {
      candidato: l.produto,
      quantidade: (atual?.quantidade ?? 0) + (l.quantidade ?? 0),
      origem: l.origem,
      destino: l.destino,
    });
    totalPorProduto.set(
      l.produto.produto,
      (totalPorProduto.get(l.produto.produto) ?? 0) + (l.quantidade ?? 0),
    );
  }
  const semSaldo = [...new Set([...somados.values()].map((s) => s.candidato))].filter(
    (c) => c.saldo !== null && c.saldo < (totalPorProduto.get(c.produto) ?? 0),
  );
  // Linhas que entram mas ainda pedem algo no cabeçalho (padrão faltando ou ruim).
  const pedemCabecalho = linhas.filter(
    (l) =>
      (l.situacao === 'ok' || l.situacao === 'escolhido') &&
      l.loc &&
      (l.loc.origem.erro || l.loc.destino.erro),
  ).length;
  const aEscolher = linhas.filter((l) => l.situacao === 'escolher').length;
  const comProblema = linhas.filter(
    (l) => l.situacao === 'invalida' || l.situacao === 'nao_encontrado',
  ).length;
  const prontos = somados.size;

  const adicionar = (): void => {
    const registro: RegistroImportacao = {
      arquivo,
      texto: atrasado,
      linhas: linhas.map((l) => ({
        linha: l.linha,
        texto: l.texto,
        codigo: l.codigo,
        quantidade: l.quantidade,
        situacao: l.situacao === 'escolher' ? 'descartada' : l.situacao,
        produto: l.produto?.produto ?? null,
        via: l.produto?.via ?? null,
        ...(l.candidatos.length > 1 ? { candidatos: l.candidatos.map((c) => c.produto) } : {}),
        origem: l.origem,
        destino: l.destino,
        motivo: l.problema,
      })),
    };
    aoAdicionar(
      [...somados.values()].map((s) => ({
        produto: { produto: s.candidato.produto, descricao: s.candidato.descricao },
        quantidade: s.quantidade,
        localizadorOrigem: s.origem,
        localizadorDestino: s.destino,
      })),
      registro,
    );
  };

  const receberArquivo = async (f: File): Promise<void> => {
    if (/\.(xlsx?|ods)$/i.test(f.name)) {
      definirAviso('Planilha: selecione as células no Excel, copie (Ctrl+C) e cole aqui.');
      return;
    }
    definirAviso(null);
    definirArquivo(f.name);
    definirTexto(await lerArquivo(f));
  };

  return (
    <div className="space-y-2.5 rounded-xl border border-borda bg-superficie2 p-3">
      <div className="flex items-center gap-2">
        <ClipboardPaste className="h-4 w-4 text-textoFraco" />
        <span className="text-[13px] font-semibold">Colar lista</span>
        <span className="text-[11.5px] text-textoFraco">
          uma linha por produto: código ou EAN, a quantidade e, se quiser, o localizador de
          origem e o de destino
        </span>
        <Botao
          variante="fantasma"
          tamanho="micro"
          className="ml-auto"
          onClick={aoFechar}
          aria-label="Fechar importação"
        >
          <X className="h-3.5 w-3.5" />
        </Botao>
      </div>

      <div
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => {
          e.preventDefault();
          const f = e.dataTransfer.files[0];
          if (f) void receberArquivo(f);
        }}
      >
        <textarea
          value={texto}
          onChange={(e) => {
            definirTexto(e.target.value);
            if (e.target.value === '') definirArquivo(null);
          }}
          rows={5}
          autoFocus
          placeholder={
            'Cole aqui. Ex.:\n' +
            '  12745 3                 → usa os localizadores do cabeçalho\n' +
            '  12745 3 56302 56307     → código qtde origem destino\n' +
            '  7897016833776;10;;56307 → célula vazia = padrão do cabeçalho\n\n' +
            'Localizador é o CÓDIGO (ex.: 56302). TAB (células do Excel), ; ou espaço separam.'
          }
          aria-label="Lista de produtos e quantidades"
          className="w-full rounded-lg border border-borda bg-superficie p-2.5 font-mono text-[13px] focus:border-primaria focus:outline-none focus:ring-4 focus:ring-primaria/12"
        />
        <div className="mt-1 flex flex-wrap items-center gap-2 text-[11.5px] text-textoFraco">
          <input
            ref={seletorArquivo}
            type="file"
            accept=".txt,.csv,text/plain,text/csv"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void receberArquivo(f);
              e.target.value = '';
            }}
          />
          <Botao variante="secundaria" tamanho="micro" onClick={() => seletorArquivo.current?.click()}>
            <Upload className="h-3.5 w-3.5" />
            Abrir .txt / .csv
          </Botao>
          <Botao
            variante="fantasma"
            tamanho="micro"
            onClick={() => baixar('modelo-transferencia-centros.csv', MODELO_LISTA)}
          >
            <Download className="h-3.5 w-3.5" />
            Baixar modelo
          </Botao>
          <span>ou solte o arquivo na caixa.</span>
          {arquivo ? <span className="font-mono">{arquivo}</span> : null}
        </div>
        {aviso ? <div className="mt-1 text-[12px] text-atencao">{aviso}</div> : null}
      </div>

      {grande ? (
        <div className="rounded-lg border border-atencao/40 bg-atencao/10 px-3 py-2 text-[12.5px]">
          {numero(lidas.length)} linhas — o limite é {numero(MAX_LINHAS)} por importação. Divida a
          lista.
        </div>
      ) : lidas.length > 0 ? (
        <>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[12px]">
            <span className="text-sucesso">
              <Check className="mr-0.5 inline h-3.5 w-3.5" />
              {numero(prontos)} produto(s) prontos
            </span>
            {aEscolher > 0 ? (
              <span className="text-atencao">{aEscolher} código(s) ambíguo(s) — escolha o produto</span>
            ) : null}
            {comProblema > 0 ? (
              <span className="text-perigo">{comProblema} linha(s) não entram</span>
            ) : null}
            {semSaldo.length > 0 ? (
              <span className="text-atencao">
                <AlertTriangle className="mr-0.5 inline h-3.5 w-3.5" />
                {semSaldo.length} sem saldo suficiente na origem
              </span>
            ) : null}
            {pedemCabecalho > 0 ? (
              <span className="text-atencao">
                {pedemCabecalho} sem localizador válido — acerte o padrão no cabeçalho
              </span>
            ) : null}
            {centroOrigem === null || centroDestino === null ? (
              <span className="text-textoFraco">
                escolha os dois centros para conferir saldo e localizadores
              </span>
            ) : null}
            {resolvendo || conferindoLocalizadores ? (
              <span className="text-textoFraco">conferindo…</span>
            ) : null}
          </div>

          {localizadores.isError ? (
            <div className="text-[12.5px] text-perigo">
              Não foi possível conferir os localizadores:{' '}
              {localizadores.error instanceof Error ? localizadores.error.message : 'erro'}
            </div>
          ) : null}

          {consulta.isError ? (
            <div className="text-[12.5px] text-perigo">
              Não foi possível conferir os códigos:{' '}
              {consulta.error instanceof Error ? consulta.error.message : 'erro'}
            </div>
          ) : null}

          {/* Scroll próprio: lista longa não empurra o botão para fora da tela. */}
          <div className="max-h-[320px] overflow-auto rounded-lg border border-borda bg-superficie">
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-superficie2 text-left text-[11px] uppercase tracking-wide text-textoFraco">
                <tr>
                  <th className="px-2 py-1.5">Lin.</th>
                  <th className="px-2 py-1.5">Lido</th>
                  <th className="px-2 py-1.5">Produto</th>
                  <th className="px-2 py-1.5 text-right">Qtde</th>
                  <th className="px-2 py-1.5 text-right" title="Saldo do produto no centro de origem">
                    Saldo orig.
                  </th>
                  <th
                    className="px-2 py-1.5"
                    title="Localizador de origem e o saldo do produto nele"
                  >
                    Sai de
                  </th>
                  <th className="px-2 py-1.5">Entra em</th>
                  <th className="w-8" />
                </tr>
              </thead>
              <tbody>
                {linhas.map((l) => {
                  const total = l.produto ? (totalPorProduto.get(l.produto.produto) ?? 0) : 0;
                  const falta =
                    l.produto?.saldo !== null && l.produto !== null && l.produto.saldo < total;
                  const fora =
                    l.situacao === 'descartada' ||
                    l.situacao === 'cabecalho' ||
                    l.situacao === 'invalida' ||
                    l.situacao === 'nao_encontrado';
                  return (
                    <tr key={l.linha} className={cn('border-t border-borda', fora && 'opacity-60')}>
                      <td className="tabular px-2 py-1 font-mono text-textoFraco">{l.linha}</td>
                      <td className="max-w-[160px] truncate px-2 py-1 font-mono" title={l.texto}>
                        {l.codigo ?? l.texto}
                      </td>
                      <td className="px-2 py-1">
                        {l.situacao === 'cabecalho' ? (
                          <span className="text-textoFraco">cabeçalho — ignorado</span>
                        ) : l.situacao === 'invalida' ? (
                          <span className="text-perigo">{l.problema}</span>
                        ) : l.situacao === 'nao_encontrado' ? (
                          resolvendo ? (
                            <span className="text-textoFraco">…</span>
                          ) : (
                            <span className="text-perigo">código não encontrado</span>
                          )
                        ) : l.candidatos.length > 1 && l.situacao !== 'descartada' ? (
                          <select
                            value={escolhas[l.linha] ?? ''}
                            onChange={(e) =>
                              definirEscolhas((a) => ({ ...a, [l.linha]: Number(e.target.value) }))
                            }
                            aria-label={`Produto da linha ${l.linha}`}
                            className={cn(
                              'w-full rounded-md border bg-superficie px-1.5 py-1 text-[12.5px]',
                              l.situacao === 'escolher' ? 'border-atencao' : 'border-borda',
                            )}
                          >
                            <option value="">ambíguo — qual produto?</option>
                            {l.candidatos.map((c) => (
                              <option key={c.produto} value={c.produto}>
                                {c.produto} · {c.descricao ?? 'sem descrição'} (
                                {c.via === 'ean' ? 'pelo EAN' : 'pelo código'})
                              </option>
                            ))}
                          </select>
                        ) : l.produto ? (
                          <span className="flex min-w-0 items-center gap-1.5">
                            <span className="tabular shrink-0 font-mono font-bold">
                              {l.produto.produto}
                            </span>
                            <span className="truncate" title={l.produto.descricao ?? ''}>
                              {l.produto.descricao ?? '—'}
                            </span>
                            {l.produto.via === 'ean' ? (
                              <span className="shrink-0 text-[10.5px] text-textoFraco">EAN</span>
                            ) : null}
                          </span>
                        ) : (
                          <span className="text-textoFraco">descartada</span>
                        )}
                      </td>
                      <td className="tabular px-2 py-1 text-right font-mono">
                        {l.quantidade === null ? '—' : numero(l.quantidade)}
                      </td>
                      <td
                        className={cn(
                          'tabular px-2 py-1 text-right font-mono',
                          falta && !fora && 'font-bold text-atencao',
                        )}
                        title={falta && !fora ? `Pedido ${numero(total)} no total` : undefined}
                      >
                        {l.produto && l.produto.saldo !== null ? numero(l.produto.saldo) : '—'}
                      </td>
                      <td className="max-w-[170px] px-2 py-1">
                        <PontaDaLinha
                          ponta="origem"
                          proprio={l.origem}
                          conferida={fora ? null : (l.loc?.origem ?? null)}
                          saldo={
                            !fora && l.loc && l.loc.saldoOrigem !== null ? (
                              <span
                                className={cn(
                                  'ml-1.5',
                                  l.loc.totalNaOrigem > l.loc.saldoOrigem &&
                                    'font-bold text-atencao',
                                )}
                                title={
                                  l.loc.totalNaOrigem > (l.quantidade ?? 0)
                                    ? `A lista tira ${l.loc.totalNaOrigem} deste produto deste localizador, somando as linhas`
                                    : 'Saldo do produto neste localizador'
                                }
                              >
                                · saldo {numero(l.loc.saldoOrigem)}
                              </span>
                            ) : null
                          }
                        />
                      </td>
                      <td className="max-w-[170px] px-2 py-1">
                        <PontaDaLinha
                          ponta="destino"
                          proprio={l.destino}
                          conferida={fora ? null : (l.loc?.destino ?? null)}
                        />
                      </td>
                      <td className="px-1 py-1">
                        {l.situacao !== 'cabecalho' ? (
                          <button
                            type="button"
                            onClick={() =>
                              definirDescartadas((a) => {
                                const n = new Set(a);
                                if (n.has(l.linha)) n.delete(l.linha);
                                else n.add(l.linha);
                                return n;
                              })
                            }
                            className="rounded p-1 text-textoFraco hover:bg-superficie2 hover:text-texto"
                            aria-label={
                              l.situacao === 'descartada'
                                ? `Voltar a linha ${l.linha}`
                                : `Descartar a linha ${l.linha}`
                            }
                            title={l.situacao === 'descartada' ? 'voltar' : 'descartar'}
                          >
                            {l.situacao === 'descartada' ? (
                              <ClipboardPaste className="h-3.5 w-3.5" />
                            ) : (
                              <X className="h-3.5 w-3.5" />
                            )}
                          </button>
                        ) : null}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <span className="text-[11.5px] text-textoFraco">
              {aEscolher > 0
                ? 'Escolha o produto nas linhas ambíguas ou descarte-as.'
                : semSaldo.length > 0
                  ? 'Saldo insuficiente não impede criar — a aprovação recusa e diz quanto falta.'
                  : 'Repetidos são somados. O que foi colado fica registrado na transferência.'}
            </span>
            <Botao
              variante="primaria"
              tamanho="pequeno"
              className="ml-auto"
              disabled={prontos === 0 || aEscolher > 0 || resolvendo || conferindoLocalizadores}
              onClick={adicionar}
            >
              Adicionar {numero(prontos)} produto(s)
            </Botao>
          </div>
        </>
      ) : null}
    </div>
  );
}

/**
 * Um localizador na linha da prévia: o achado (endereço em destaque, como na
 * transferência entre localizadores), o motivo de não servir, ou o código cru
 * enquanto confere. "padrão" marca o herdado do cabeçalho.
 */
function PontaDaLinha({
  ponta,
  proprio,
  conferida,
  saldo,
}: {
  ponta: 'origem' | 'destino';
  /** O que veio na 3ª/4ª coluna. */
  proprio: string | null;
  conferida: ConferenciaLocalizadores['linhas'][number]['origem'] | null;
  saldo?: React.ReactNode;
}): JSX.Element {
  if (conferida?.posicao) {
    return (
      <Localizador
        ponta={ponta}
        p={conferida.posicao}
        extra={
          <>
            {conferida.herdada ? <span className="ml-1.5">padrão</span> : null}
            {saldo}
          </>
        }
      />
    );
  }
  if (conferida?.erro) {
    return (
      <span
        className={cn(
          'block text-[11.5px] leading-tight',
          conferida.herdada ? 'text-atencao' : 'text-perigo',
        )}
      >
        {conferida.erro}
      </span>
    );
  }
  return <span className="font-mono text-textoFraco">{proprio ?? 'padrão'}</span>;
}
